/**
 * Country flag component module.
 *
 * Wraps `country-flag-icons` to give a single `<Flag>` component that takes
 * a 2-letter ISO 3166-1 alpha-2 country code and renders the flag SVG.
 *
 * We import each flag explicitly rather than `import * as Flags from
 * 'country-flag-icons/react/3x2'` so the bundler tree-shakes unused flags.
 *
 * The flag SVGs render at the parent's text height by default. Override
 * with `style={{ width, height }}` or via the `size` prop helper. A subtle
 * border + corner radius is applied so light flags (US, JP) don't blend
 * into pale backgrounds.
 */
import React from 'react';

// 3:2 aspect ratio is the universal default. country-flag-icons also offers
// 1:1 (squared) under `country-flag-icons/react/1x1/<CC>` if needed elsewhere.
import US from 'country-flag-icons/react/3x2/US';
import GB from 'country-flag-icons/react/3x2/GB';
import DE from 'country-flag-icons/react/3x2/DE';
import IT from 'country-flag-icons/react/3x2/IT';
import ES from 'country-flag-icons/react/3x2/ES';
import PT from 'country-flag-icons/react/3x2/PT';
import FR from 'country-flag-icons/react/3x2/FR';
import CN from 'country-flag-icons/react/3x2/CN';
import TW from 'country-flag-icons/react/3x2/TW';
import JP from 'country-flag-icons/react/3x2/JP';
import KR from 'country-flag-icons/react/3x2/KR';
import SA from 'country-flag-icons/react/3x2/SA';

/**
 * Map of supported ISO 3166-1 alpha-2 country codes to their flag component.
 * Add new entries here when introducing a new country flag elsewhere in the
 * app — keeping the registry centralised means we don't end up importing the
 * same flag from two places, and tree-shaking stays predictable.
 */
const FLAG_COMPONENTS = {
  US,
  GB,
  DE,
  IT,
  ES,
  PT,
  FR,
  CN,
  TW,
  JP,
  KR,
  SA,
} as const;

export type CountryCode = keyof typeof FLAG_COMPONENTS;

/**
 * Props for `<Flag>`. We deliberately don't extend React's SVG attribute
 * types here — the underlying components from `country-flag-icons` declare
 * their props against an internal `HTMLSVGElement` type which conflicts
 * with `SVGSVGElement` event handlers. Exposing only the small surface we
 * actually need (style, className, a11y attrs) avoids that mismatch and
 * keeps the API focused on flag-rendering concerns.
 */
export interface FlagProps {
  /** ISO 3166-1 alpha-2 country code (e.g. "US", "GB"). */
  countryCode: CountryCode | string;
  /**
   * Convenience size override. Sets the SVG height in pixels (width is
   * derived from the 3:2 aspect ratio). Defaults to inheriting from the
   * parent's font-size via `1em` height.
   */
  size?: number;
  /**
   * When true (default) the flag is wrapped in a thin border and corner
   * radius so it reads as a flag against any background. Set to false to
   * render the bare SVG (e.g. inside a chip that already has a border).
   */
  bordered?: boolean;
  /** Accessible label. Defaults to the country code. */
  title?: string;
  /** Additional className (forwarded to the SVG). */
  className?: string;
  /** Inline style overrides (merged on top of the computed defaults). */
  style?: React.CSSProperties;
  /** Pass-through accessible label override. */
  'aria-label'?: string;
  /** Pass-through aria-hidden — set when the flag is purely decorative. */
  'aria-hidden'?: boolean;
}

/**
 * Renders the flag for the given ISO 3166-1 alpha-2 code. Returns null for
 * unknown codes so callers can use it inline without explicit guards.
 */
export function Flag({
  countryCode,
  size,
  bordered = true,
  title,
  className,
  style,
  ...aria
}: FlagProps) {
  const code = countryCode.toUpperCase() as CountryCode;
  const FlagComponent = FLAG_COMPONENTS[code];
  if (!FlagComponent) return null;

  const dim: React.CSSProperties = size
    ? { height: size, width: size * 1.5 }
    : { height: '1em', width: '1.5em' };

  const borderStyle: React.CSSProperties = bordered
    ? {
        borderRadius: 2,
        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
      }
    : {};

  return (
    <FlagComponent
      title={title ?? code}
      className={className}
      aria-label={aria['aria-label']}
      aria-hidden={aria['aria-hidden']}
      style={{
        display: 'inline-block',
        verticalAlign: '-0.15em',
        flexShrink: 0,
        ...dim,
        ...borderStyle,
        ...style,
      }}
    />
  );
}

/**
 * Lookup helper for callers that want the raw component (e.g. to render
 * inside an `<Avatar>` slot). Returns `undefined` for unknown codes.
 */
export function getFlagComponent(countryCode: string) {
  return FLAG_COMPONENTS[countryCode.toUpperCase() as CountryCode];
}
